// Summarizer constants — shared across HubDO, ConversationDO, and buildAgentSettings.

export const RESERVED_SUMMARY_STATE_SLUG = 'conversation_summary' as const;
export const DEFAULT_SUMMARIZATION_THRESHOLD_TOKENS = 120000;
export const SUMMARIZATION_THRESHOLD_MIN = 1000;
export const SUMMARIZATION_THRESHOLD_MAX = 1_000_000;
export const SUMMARIZER_FAILURE_THRESHOLD = 3;
export const SUMMARIZER_DEBOUNCE_MS = 60_000;

/** Strict invariant — load-bearing for expand_summary. Extra fields allowed; anchors are not. */
export const SUMMARIZER_INVARIANT_SCHEMA = {
  type: 'object',
  required: ['sections'],
  properties: {
    sections: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'message_id_start', 'message_id_end'],
        properties: {
          id: { type: 'string', minLength: 1 },
          message_id_start: { type: 'string', minLength: 1 },
          message_id_end: { type: 'string', minLength: 1 },
        },
      },
    },
  },
} as const;

/** Default user-editable schema seeded for new hubs. */
export const SUMMARIZER_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['sections'],
  properties: {
    sections: {
      type: 'array',
      description: 'Topically-grouped summary units covering the conversation up to the verbatim tail. Each section carries a message-range anchor used by expand_summary.',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['id', 'topic', 'summary', 'message_id_start', 'message_id_end'],
        properties: {
          id: {
            type: 'string',
            minLength: 1,
            description: 'Stable identifier for this section (e.g. "sec_01"). Used by expand_summary to fetch original messages.',
          },
          topic: {
            type: 'string',
            minLength: 1,
            description: 'Short topic label (1-5 words) describing what this section covers.',
          },
          summary: {
            type: 'string',
            minLength: 1,
            description: 'Concise prose summary of what happened in this section. Preserve concrete details (names, prices, decisions); compress filler.',
          },
          message_id_start: {
            type: 'string',
            minLength: 1,
            description: 'message_id of the FIRST message included in this section.',
          },
          message_id_end: {
            type: 'string',
            minLength: 1,
            description: 'message_id of the LAST message included in this section.',
          },
          key_decisions: {
            type: 'array',
            items: { type: 'string' },
            description: 'Decisions or commitments made in this section (e.g. "agreed price: $79/yr").',
          },
          open_threads: {
            type: 'array',
            items: { type: 'string' },
            description: 'Unresolved threads opened in this section that may need follow-up.',
          },
        },
      },
    },
  },
} as const;

/** System prompt for the summarizer agent. */
export const SUMMARIZER_INSTRUCTIONS = `You are the conversation summarizer. Your job is to compress older messages of a conversation into a structured JSON summary while preserving everything that matters for the agents who will continue the conversation.

Rules:
1. Read the entire input. Each message has a \`message_id\` attribute — record them exactly.
2. Group messages into topical sections. A section is one coherent thread (a question being resolved, a topic being discussed, a decision being made). Aim for one section per topic; don't fragment.
3. For each section, set:
   - \`id\`: a short stable identifier (e.g. "sec_01", "sec_02"). Use sequential numbering.
   - \`topic\`: 1–5 words naming the thread.
   - \`summary\`: concise prose. Preserve concrete details (exact names, prices, IDs, decisions, commitments, dates). Compress filler. Do NOT invent facts.
   - \`message_id_start\` and \`message_id_end\`: the exact message_id of the FIRST and LAST messages you included in this section. These are load-bearing — they must reference real messages from the input.
   - \`key_decisions\`: decisions or commitments made (optional).
   - \`open_threads\`: unresolved items that may need follow-up (optional).
4. If a prior summary is provided, the new summary should fold it in: re-use stable section ids where the underlying messages are unchanged, add new sections for the new messages. Replace the prior summary in its entirety with your output.
5. Emit ONLY valid JSON matching the schema. No prose preamble, no markdown fences.
6. Tone: factual, third-person, terse.`;

/** Appended to non-background agents' system prompt when the hub has a conversation_summary state. */
export const SUMMARIZER_PROMPT_ADDENDUM = `
---
**Conversation summary available.** Older parts of this conversation may have been compressed into a structured summary delivered as a \`<conversation_summary>\` block prepended to the message history. Each section in that summary carries an \`id\` and a message-range anchor.

If a section's summary is insufficient for your current task — for example, you need the user's exact wording, the precise text of a previous decision, or details that aren't captured in the prose — call the \`expand_summary\` tool with the section's \`id\` to retrieve the original messages. Don't confabulate from a compressed summary when the originals are one tool call away.`;
